import React, { useState } from 'react';
import { Mail, MapPin, Send, Shield } from 'lucide-react';
import { SEO } from '../components/layout/SEO';
import { Section } from '../components/ui/Section';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Textarea } from '../components/ui/Textarea';
import { Card, CardContent } from '../components/ui/Card';
import { FadeIn } from '../components/ui/FadeIn';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
export function ContactPage() {
  const {
    t,
    isRTL
  } = useLanguage();
  const [formState, setFormState] = useState({
    firstName: '',
    lastName: '',
    email: '',
    subject: '',
    message: ''
  });
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Form submitted to info@kmauk.org:', formState);
    alert('Thank you for your message. We will get back to you shortly.');
  };
  return <>
      <SEO title={t('nav.contact')} description={t('contact.hero.subtitle')} data-id="element-297" />

      <div className="bg-primary-900 text-white py-16 md:py-20" data-id="element-298">
        <div className="container mx-auto px-4 text-center" data-id="element-299">
          <FadeIn data-id="element-300">
            <h1 className="text-4xl md:text-5xl font-serif font-bold mb-4" data-id="element-301">
              {t('contact.hero.title')}
            </h1>
            <p className="text-xl text-primary-100 max-w-2xl mx-auto" data-id="element-302">
              {t('contact.hero.subtitle')}
            </p>
          </FadeIn>
        </div>
      </div>

      <Section data-id="element-303">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-6xl mx-auto" data-id="element-304">
          {/* Contact Information */}
          <FadeIn data-id="element-305">
            <div className="space-y-8" data-id="element-306">
              <div data-id="element-307">
                <h2 className="text-2xl font-serif font-bold text-gray-900 mb-6" data-id="element-308">
                  {t('contact.getInTouch.title')}
                </h2>
                <p className="text-gray-600 mb-8 leading-relaxed" data-id="element-309">
                  {t('contact.getInTouch.subtitle')}
                </p>
              </div>

              <div className="space-y-6" data-id="element-310">
                <Card className="border-primary-100" data-id="element-311">
                  <CardContent className="flex items-start p-6" data-id="element-312">
                    <div className={`flex-shrink-0 h-12 w-12 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center ${isRTL ? 'ml-4' : 'mr-4'}`} data-id="element-313">
                      <MapPin className="h-6 w-6" data-id="element-314" />
                    </div>
                    <div data-id="element-315">
                      <h3 className="font-bold text-gray-900 mb-2" data-id="element-316">
                        {t('contact.address.title')}
                      </h3>
                      <p className="text-gray-600 leading-relaxed" data-id="element-317">
                        17 Honeybourne
                        <br data-id="element-318" />
                        Bishop's Stortford
                        <br data-id="element-319" />
                        CM23 4EF
                        <br data-id="element-320" />
                        United Kingdom
                      </p>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-primary-100" data-id="element-321">
                  <CardContent className="flex items-center p-6" data-id="element-322">
                    <div className={`flex-shrink-0 h-12 w-12 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center ${isRTL ? 'ml-4' : 'mr-4'}`} data-id="element-323">
                      <Mail className="h-6 w-6" data-id="element-324" />
                    </div>
                    <div data-id="element-325">
                      <h3 className="font-bold text-gray-900 mb-2" data-id="element-326">
                        {t('contact.email.title')}
                      </h3>
                      <a href="mailto:info@kmauk.org" className="text-primary-700 hover:underline text-lg font-medium" data-id="element-327">
                        info@kmauk.org
                      </a>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Reassurance */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-6" data-id="element-328">
                <div className="flex items-start gap-3" data-id="element-329">
                  <Shield className="h-5 w-5 text-green-600 shrink-0 mt-0.5" data-id="element-330" />
                  <div data-id="element-331">
                    <h4 className="font-bold text-gray-900 mb-2" data-id="element-332">
                      {t('contact.privacy.title')}
                    </h4>
                    <p className="text-sm text-gray-700 leading-relaxed mb-2" data-id="element-333">
                      {t('contact.privacy.description')}
                    </p>
                    <Link to="/privacy" className="text-sm font-medium text-green-700 hover:underline" data-id="element-334">
                      {t('contact.privacy.link')}
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </FadeIn>

          {/* Contact Form */}
          <FadeIn delay={0.2} data-id="element-335">
            <Card className="border-primary-100 shadow-lg" data-id="element-336">
              <CardContent className="p-8" data-id="element-337">
                <h3 className="text-xl font-bold text-gray-900 mb-6" data-id="element-338">
                  {t('contact.form.title')}
                </h3>
                <form className="space-y-5" onSubmit={handleSubmit} data-id="element-339">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-340">
                    <Input label={t('contact.form.firstName')} placeholder="John" value={formState.firstName} onChange={e => setFormState({
                    ...formState,
                    firstName: e.target.value
                  })} required data-id="element-341" />
                    <Input label={t('contact.form.lastName')} placeholder="Doe" value={formState.lastName} onChange={e => setFormState({
                    ...formState,
                    lastName: e.target.value
                  })} required data-id="element-342" />
                  </div>
                  <Input label={t('contact.form.email')} type="email" placeholder="john.doe@example.com" value={formState.email} onChange={e => setFormState({
                  ...formState,
                  email: e.target.value
                })} required data-id="element-343" />
                  <Input label={t('contact.form.subject')} placeholder={t('contact.form.subjectPlaceholder')} value={formState.subject} onChange={e => setFormState({
                  ...formState,
                  subject: e.target.value
                })} required data-id="element-344" />
                  <Textarea label={t('contact.form.message')} placeholder={t('contact.form.messagePlaceholder')} rows={6} value={formState.message} onChange={e => setFormState({
                  ...formState,
                  message: e.target.value
                })} required data-id="element-345" />
                  <Button type="submit" className="w-full" size="lg" data-id="element-346">
                    <Send className={`h-5 w-5 ${isRTL ? 'ml-2' : 'mr-2'}`} data-id="element-347" />
                    {t('contact.form.submit')}
                  </Button>
                  <p className="text-xs text-gray-500 text-center" data-id="element-348">
                    {t('contact.form.note')}
                  </p>
                </form>
              </CardContent>
            </Card>
          </FadeIn>
        </div>
      </Section>

      {/* Quick Links */}
      <Section background="gray" className="text-center" data-id="element-349">
        <FadeIn data-id="element-350">
          <h2 className="text-2xl font-serif font-bold text-gray-900 mb-8" data-id="element-351">
            {t('contact.quickLinks.title')}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-4xl mx-auto" data-id="element-352">
            <Card className="hover:shadow-md transition-shadow" data-id="element-353">
              <CardContent className="p-6 text-center" data-id="element-354">
                <h3 className="font-bold text-gray-900 mb-2" data-id="element-355">
                  {t('contact.quickLinks.membership.title')}
                </h3>
                <p className="text-sm text-gray-600 mb-4" data-id="element-356">
                  {t('contact.quickLinks.membership.subtitle')}
                </p>
                <a href="/membership" className="text-primary-700 hover:underline text-sm font-medium" data-id="element-357">
                  {t('contact.quickLinks.membership.link')}
                </a>
              </CardContent>
            </Card>

            <Card className="hover:shadow-md transition-shadow" data-id="element-358">
              <CardContent className="p-6 text-center" data-id="element-359">
                <h3 className="font-bold text-gray-900 mb-2" data-id="element-360">
                  {t('contact.quickLinks.donations.title')}
                </h3>
                <p className="text-sm text-gray-600 mb-4" data-id="element-361">
                  {t('contact.quickLinks.donations.subtitle')}
                </p>
                <a href="/charity" className="text-primary-700 hover:underline text-sm font-medium" data-id="element-362">
                  {t('contact.quickLinks.donations.link')}
                </a>
              </CardContent>
            </Card>

            <Card className="hover:shadow-md transition-shadow" data-id="element-363">
              <CardContent className="p-6 text-center" data-id="element-364">
                <h3 className="font-bold text-gray-900 mb-2" data-id="element-365">
                  {t('contact.quickLinks.events.title')}
                </h3>
                <p className="text-sm text-gray-600 mb-4" data-id="element-366">
                  {t('contact.quickLinks.events.subtitle')}
                </p>
                <a href="/events" className="text-primary-700 hover:underline text-sm font-medium" data-id="element-367">
                  {t('contact.quickLinks.events.link')}
                </a>
              </CardContent>
            </Card>
          </div>
        </FadeIn>
      </Section>
    </>;
}